import { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { PiggyBank, Pencil } from 'lucide-react';
import { Sensitive } from '../Sensitive';

const fmtUSD = (n) => `$${Math.round(Number(n || 0)).toLocaleString()}`;
const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(v) ? `${v}T12:00:00` : v);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export const AnnuityProductionSection = ({ data, policies = [], canEdit = false, onEdit, title = 'Issued Annuity Production', testId }) => {
  const [search, setSearch] = useState('');
  const totalAp = Number(data?.issued_annuity_ap) || 0;
  const count = Number(data?.annuity_policies) || 0;
  const avg = count > 0 ? Math.round(totalAp / count) : 0;
  const carriers = data?.annuity_by_carrier || [];
  const rows = [...policies]
    .filter((p) => !search || `${p.insured_name || ''} ${p.carrier || ''} ${p.policy_number || ''} ${p.agent_name || ''}`.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => (b.date_issued || b.date_submitted || '').localeCompare(a.date_issued || a.date_submitted || ''));

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} data-testid={testId}>
      <Card className="overflow-hidden border-0 bg-gradient-to-br from-[#0B1437] via-[#0E1839] to-[#0B1437] text-white shadow-[0px_18px_40px_rgba(112,144,176,0.18)]">
        <CardHeader className="relative pb-2 pt-5 px-5">
          <div className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-[#01F5C4]/20 blur-3xl" />
          <div className="relative flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white/10">
                <PiggyBank className="h-5 w-5 text-[#01F5C4]" />
              </div>
              <div>
                <CardTitle className="font-['Manrope'] text-base font-extrabold text-white">{title}</CardTitle>
                <p className="text-[11px] text-white/50">Issued annuity business, tracked separately from life AP</p>
              </div>
            </div>
            {rows.length > 0 && (
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search annuities…"
                className="w-full rounded-md border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-[#01F5C4]/40 sm:w-52"
                data-testid={`${testId}-search`}
              />
            )}
          </div>
        </CardHeader>
        <CardContent className="relative space-y-4 px-5 pb-5">
          <div className="grid grid-cols-3 divide-x divide-white/10 rounded-2xl border border-white/10 bg-white/5">
            {[
              { label: 'Issued Annuity', value: fmtUSD(totalAp), id: 'total' },
              { label: 'Annuity Policies', value: count, id: 'count' },
              { label: 'Avg per Policy', value: fmtUSD(avg), id: 'avg' },
            ].map((s) => (
              <div key={s.id} className="px-4 py-3">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-white/45">{s.label}</p>
                <Sensitive as="p" className="font-['Manrope'] text-xl font-extrabold text-white" data-testid={`${testId}-${s.id}`}>
                  {s.value}
                </Sensitive>
              </div>
            ))}
          </div>

          {count === 0 ? (
            <p className="py-6 text-center text-sm text-white/50">No issued annuities in the selected period.</p>
          ) : (
            <div className="grid gap-4 lg:grid-cols-5">
              <div className="space-y-2 lg:col-span-2">
                <p className="text-[10px] font-bold uppercase tracking-wider text-white/45">By carrier</p>
                {carriers.map((c) => {
                  const pct = totalAp > 0 ? Math.round(((c.issued_ap || 0) / totalAp) * 100) : 0;
                  return (
                    <div key={c.carrier} className="rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2">
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-semibold text-white">{c.carrier}</span>
                        <Sensitive className="font-bold text-[#01F5C4]">{fmtUSD(c.issued_ap)}</Sensitive>
                      </div>
                      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-white/10">
                        <div className="h-full rounded-full bg-gradient-to-r from-[#01F5C4] to-[#23ACD5]" style={{ width: `${Math.max(pct, 3)}%` }} />
                      </div>
                      <p className="mt-1 text-[10px] text-white/45">{c.policies} {c.policies === 1 ? 'policy' : 'policies'} · {pct}%</p>
                    </div>
                  );
                })}
              </div>
              {rows.length > 0 && (
                <div className="overflow-x-auto rounded-xl border border-white/10 lg:col-span-3">
                  <table className="w-full text-xs">
                    <thead className="bg-white/5 text-[10px] uppercase tracking-wide text-white/45">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">Client</th>
                        {rows.some((r) => r.agent_name) && <th className="px-3 py-2 text-left font-medium">Agent</th>}
                        <th className="px-3 py-2 text-left font-medium">Carrier</th>
                        <th className="px-3 py-2 text-right font-medium">Amount</th>
                        <th className="hidden px-3 py-2 text-left font-medium md:table-cell">Issued</th>
                        {canEdit && <th className="w-10 px-3 py-2" />}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((p, i) => (
                        <tr key={p.id || i} className="border-t border-white/5 hover:bg-white/5" data-testid={`${testId}-row-${p.policy_number || i}`}>
                          <td className="px-3 py-2 font-medium text-white">{p.insured_name || '—'}</td>
                          {rows.some((r) => r.agent_name) && <td className="px-3 py-2 text-white/70">{p.agent_name || '—'}</td>}
                          <td className="px-3 py-2 text-white/70">{p.carrier || '—'}</td>
                          <td className="px-3 py-2 text-right font-bold text-[#01F5C4]"><Sensitive>{fmtUSD(p.issued_paid_ap ?? p.annual_premium)}</Sensitive></td>
                          <td className="hidden px-3 py-2 text-white/50 md:table-cell">{fmtDate(p.date_issued || p.date_submitted)}</td>
                          {canEdit && (
                            <td className="px-3 py-2 text-right">
                              <button type="button" onClick={() => onEdit?.(p)} data-testid={`${testId}-edit-btn-${p.policy_number || i}`} className="rounded-md p-1 text-white/50 hover:bg-white/10 hover:text-white" title="Edit">
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};
